import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PieChart from './PieChart';
import MeanChart from './MeanChart';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { checkBGValue } from '../../../../utils/measurementUtils';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../../utils/reportEmailSend';
import { downloadBlob, printElement, toCsv } from '../../../../utils/reportExportUtils';
import { exportMeasurementTablesPdf } from '../../../../utils/measurementTablePdfExport';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import {
  DEFAULT_MEASUREMENT_QUERY,
  isFromToIncomplete,
  isMeasurementQueryDirty,
} from '../../../../utils/queryPeriodFilters';
import { useChartContainerWidth } from '../../../../hooks/useChartContainerWidth';
import { formatPhoneForDisplay } from '../../../../utils/phoneUtils';
import '../../MeasurementShell.css';
import './DayChart.css';

const DayChart = ({ practiceId: propPracticeId, patientId: propPatientId }) => {
  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const patientId = propPatientId || params.patientId;
  const location = useLocation();

  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
  const [patientData, setPatientData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { updateBreadcrumbs } = useBreadcrumb();

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setQueryType('dateRange');
      setDateRange('Last 30 days');
      setFromDate('');
      setToDate('');
      fetchPatientData();
      fetchScheduleTargets();
    }
  }, [practiceId, patientId]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId]);

  // Breadcrumbs effect
  useEffect(() => {
    if (patientData?.patient && practiceId && patientId) {
      const fromDashboard = location.state?.fromDashboard;
      const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
      const basePath = fromDashboard ? '/dashboard' : '/practices';

      updateBreadcrumbs([
        rootCrumb,
        { label: patientData.patient.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
        { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },
        { 
          label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient', 
          path: `/practices/${practiceId}/patients/${patientId}`,
          state: { fromDashboard }
        },
        { label: 'Blood Glucose Chart', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, updateBreadcrumbs, location.state]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        } catch {
          // ignore cache write errors
        }
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else {
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (isAllHistory || (fromDateParam && toDateParam)) {
        const response = await measurementService.getBloodGlucoseByDateRange(
          practiceId,
          patientId,
          isAllHistory ? undefined : fromDateParam,
          isAllHistory ? undefined : toDateParam,
          'daily_average'
        );

        if (response.data?.success) {
          setMeasurements(response.data.data || []);
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements();
  };

  const handleReset = () => {
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
  };

  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy: DEFAULT_MEASUREMENT_QUERY.sortBy,
  });

  const patientName = patientData?.patient 
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth 
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';
  const patientVitals = patientData?.patient?.vitals || '';

  // Get target for a period
  const getTargetForPeriod = (periodName) => {
    if (!scheduleTargets?.bloodGlucoseTargetRange) return { min: 60, max: 110 };
    const targets = scheduleTargets.bloodGlucoseTargetRange;
    const normalizeTargetKey = (value) =>
      String(value || '')
        .toLowerCase()
        .replace(/[\s_-]/g, '')
        .replace(/targetranges?$/, '')
        .replace(/targets?$/, '');
    const normalizedPeriod = normalizeTargetKey(periodName);
    const toRange = (target, fallbackMin, fallbackMax) => {
      const min = Number(target?.minimum ?? target?.min);
      const max = Number(target?.maximum ?? target?.max);
      return {
        min: Number.isNaN(min) ? fallbackMin : min,
        max: Number.isNaN(max) ? fallbackMax : max
      };
    };

    // Prefer period-specific target rows when present.
    const periodSpecific = targets.find((t) =>
      normalizeTargetKey(t.name).includes(normalizedPeriod)
    );
    if (periodSpecific) return toRange(periodSpecific, 60, 110);
    
    // Legacy Before-Meal / After-Meal shared targets
    if (
      normalizedPeriod === normalizeTargetKey('Before-Meal') ||
      ['beforebreakfast', 'beforelunch', 'beforedinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizeTargetKey(t.name).includes('beforemeal')
      ) || targets[0];
      return toRange(target, 60, 110);
    }
    
    if (
      normalizedPeriod === normalizeTargetKey('After-Meal') ||
      ['afterbreakfast', 'afterlunch', 'afterdinner'].includes(normalizedPeriod)
    ) {
      const target = targets.find(t => 
        normalizeTargetKey(t.name).includes('aftermeal')
      ) || targets[0];
      return toRange(target, 70, 140);
    }
    
    // Find target by period name or use first as default
    const period = scheduleTargets.bloodGlucoseTimePeriod?.find(
      (p) => normalizeTargetKey(p.name) === normalizedPeriod
    );
    const target = targets.find(
      (t) =>
        normalizeTargetKey(t.name) === normalizedPeriod ||
        normalizeTargetKey(t.name).includes(normalizedPeriod) ||
        normalizedPeriod.includes(normalizeTargetKey(t.name)) ||
        normalizeTargetKey(t.periodName) === normalizedPeriod ||
        t.id === period?.id
    ) || targets[0];
    
    const min = Number(target?.minimum ?? target?.min);
    const max = Number(target?.maximum ?? target?.max);
    return {
      min: Number.isNaN(min) ? 60 : min,
      max: Number.isNaN(max) ? 110 : max
    };
  };

  // Calculate chart data - show all individual measurements by time of day
  const chartData = useMemo(() => {
    if (!measurements || measurements.length === 0) return { dataPoints: [] };

    // Create data points for all measurements
    const dataPoints = [];
    
    measurements.forEach(m => {
      const bgValue = parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value);
      if (isNaN(bgValue)) return;
      
      const measureTime = m.measurement_time || 
                         m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                         m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                         '';
      
      if (!measureTime) return;
      
      const [hours, minutes] = measureTime.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return;
      
      const timeInMinutes = hours * 60 + minutes;
      const period = m.period_name || m.period || m.measure_note || m.note || '';
      const target = getTargetForPeriod(period || 'Before-Meal');
      const status = checkBGValue(bgValue, target);
      
      dataPoints.push({
        timeInMinutes,
        value: bgValue,
        status,
        date: m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0],
        time: measureTime,
        period
      });
    });

    // Sort by time
    dataPoints.sort((a, b) => a.timeInMinutes - b.timeInMinutes);

    return { dataPoints };
  }, [measurements, scheduleTargets]);

  // Chart dimensions and scaling
  const [chartAreaRef, chartWidth] = useChartContainerWidth(480, 800);
  const chartHeight = 250;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 400; // Maximum BG value in mg/dL
  const minY = 0;
  const maxTime = 24 * 60; // 24 hours in minutes

  // Scale functions
  const scaleX = (timeInMinutes) => {
    return padding.left + (timeInMinutes / maxTime) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  // Format time for display
  const formatTime = (timeInMinutes) => {
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Get color based on status
  const getPointColor = (status) => {
    if (status === 'high') return '#d32f2f'; // Red - Above Target
    if (status === 'low') return '#03a9f4'; // Light Blue - Below Target
    return '#1976d2'; // Dark Blue - In-Range
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState(null);

  const handlePrint = () => {
    try {
      const el = document.getElementById('bgdc-pdf-content');
      printElement(el, `Blood Glucose Chart - ${patientName}`);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const exportBaseName = `BG-Chart-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`;

  const handleExportPdf = () => {
    if (!chartData.dataPoints.length) {
      toast.warning('No chart data to export');
      return;
    }
    const rows = chartData.dataPoints.map((point) => [
      point.date || '',
      formatTime(point.timeInMinutes),
      point.period || '',
      Math.round(point.value),
      point.status === 'high' ? 'Above Target' : point.status === 'low' ? 'Below Target' : 'In-Range'
    ]);
    exportMeasurementTablesPdf({
      filename: `${exportBaseName}.pdf`,
      title: 'Blood Glucose Chart Report',
      patient: { name: patientName, dob: patientDOB, phone: formatPhoneForDisplay(patientData?.patient?.phone_number), device: patientData?.device_serial || '' },
      tables: [
        {
          title: 'Day Chart Readings',
          head: [['Date', 'Time', 'Period', 'Blood Glucose (mg/dL)', 'Status']],
          body: rows.length ? rows : [['No data available', '', '', '', '']]
        }
      ]
    });
    toast.success('Exported to PDF');
  };

  const handleExportCsv = () => {
    if (!chartData.dataPoints.length) {
      toast.warning('No chart data to export');
      return;
    }
    const rows = [['Date', 'Time', 'Period', 'Blood Glucose (mg/dL)', 'Status']];
    chartData.dataPoints.forEach((point) => {
      rows.push([
        point.date || '',
        formatTime(point.timeInMinutes),
        point.period || '',
        Math.round(point.value),
        point.status === 'high' ? 'Above Target' : point.status === 'low' ? 'Below Target' : 'In-Range'
      ]);
    });
    downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), `${exportBaseName}.csv`);
    toast.success('Exported to CSV');
  };

  return (
    <div className="bg-day-chart-page">
      <MeasurementChartHeader
        practiceId={practiceId}
        patientId={patientId}
        vitals={patientVitals}
        activeVital="blood-glucose"
        patientName={patientName}
        patientDOB={patientDOB}
        queryType={queryType}
        setQueryType={setQueryType}
        dateRange={dateRange}
        setDateRange={setDateRange}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onQuery={handleQuery}
        onReset={handleReset}
        queryDisabled={queryLoading}
        resetDisabled={isResetDisabled}
        onPrint={handlePrint}
        onExportPdf={handleExportPdf}
        onExportCsv={handleExportCsv}
        onEmail={() => setShowEmailModal(true)}
        hasData={measurements.length > 0}
      />

      {/* Charts scrollable container */}
      <div className="bgdc-charts-scroll" id="bgdc-pdf-content">
      {/* Day Chart Section */}
      <div className="bp-section bgdc-chart-section">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Daily Trend Chart</h2>
        </div>
        <div className="bp-card">
          <div className="bp-card-body">
        <div className="bgdc-chart-container">
          <div className="bgdc-chart-title">Standard Day Chart of Blood Glucose</div>
          <div className="bgdc-chart-legend">
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#d32f2f' }}></div>
              <span>Above Target</span>
            </div>
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#03a9f4' }}></div>
              <span>Below Target</span>
            </div>
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#1976d2' }}></div>
              <span>In-Range</span>
            </div>
          </div>
          <div className="bgdc-chart-area">
            {loading || queryLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading chart data...</div>
            ) : chartData.dataPoints.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
            ) : (
              <div className="bgdc-chart-placeholder">
                <div className="bgdc-y-axis">
                  {[400, 350, 300, 250, 200, 150, 100, 50, 0].map(value => (
                    <span key={value}>{value}</span>
                  ))}
                </div>
                <div className="bgdc-chart-grid" ref={chartAreaRef}>
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    width={chartWidth}
                    height={chartHeight}
                    preserveAspectRatio="xMidYMid meet"
                    className="bgdc-chart-svg"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  >
                    {/* Grid lines */}
                    {[0, 50, 100, 150, 200, 250, 300, 350, 400].map(value => {
                      const y = scaleY(value);
                      if (y == null) return null;
                      return (
                        <line
                          key={value}
                          x1={padding.left}
                          y1={y}
                          x2={chartWidth - padding.right}
                          y2={y}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                        />
                      );
                    })}
                    {/* Data points */}
                    {chartData.dataPoints.map((point, index) => {
                      const x = scaleX(point.timeInMinutes);
                      const y = scaleY(point.value);
                      if (y == null) return null;
                      
                      const color = getPointColor(point.status);
                      
                      const handleMouseEnter = (e) => {
                        const rect = chartAreaRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        
                        setTooltip({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          value: point.value,
                          time: formatTime(point.timeInMinutes),
                          status: point.status,
                          points: point.points
                        });
                      };
                      
                      const handleMouseLeave = () => {
                        setTooltip(null);
                      };

                      const handleClick = (e) => {
                        e.stopPropagation();
                        handleMouseEnter(e);
                      };

                      return (
                        <circle
                          key={`point-${point.timeInMinutes}-${index}`}
                          cx={x}
                          cy={y}
                          r="6"
                          fill={color}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                          onClick={handleClick}
                        />
                      );
                    })}
                  </svg>
                  <div className="bgdc-x-axis">
                    {/* Show time labels at intervals */}
                    {[0, 6, 12, 18, 24].map(hour => {
                      const timeInMinutes = hour * 60;
                      return (
                        <span key={hour} style={{ left: `${(timeInMinutes / maxTime) * 100}%` }}>
                          {formatTime(timeInMinutes)}
                        </span>
                      );
                    })}
                  </div>
                  {/* Tooltip */}
                  {tooltip && (
                    <div
                      className="bgdc-tooltip"
                      style={{
                        left: `${tooltip.x}px`,
                        top: `${tooltip.y - 80}px`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="bgdc-tooltip-content">
                        <div className="bgdc-tooltip-time">{tooltip.time}</div>
                        <div className="bgdc-tooltip-item">
                          <span className="bgdc-tooltip-label">Blood Glucose:</span>
                          <span className="bgdc-tooltip-value">{tooltip.value.toFixed(0)} mg/dL</span>
                        </div>
                        <div className="bgdc-tooltip-item">
                          <span className="bgdc-tooltip-label">Status:</span>
                          <span className="bgdc-tooltip-value">
                            {tooltip.status === 'high' ? 'Above Target' : tooltip.status === 'low' ? 'Below Target' : 'In-Range'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="bgdc-y-axis-label">Blood Glucose mg/dL</div>
        </div>
          </div>
        </div>
      </div>

      {/* Pie Chart Section */}
      <div className="bp-section bgdc-pie-chart-wrapper">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Pie Chart</h2>
        </div>
        <div className="bp-card">
          <div className="bp-card-body">
            <PieChart practiceId={practiceId} patientId={patientId} />
          </div>
        </div>
      </div>

      {/* Mean Chart Section */}
      <div className="bp-section bgdc-mean-chart-wrapper">
        <div className="bp-section-title-row">
          <h2 className="bp-section-title">Mean Chart</h2>
        </div>
        <div className="bp-card">
          <div className="bp-card-body">
            <MeanChart practiceId={practiceId} patientId={patientId} embedded />
          </div>
        </div>
      </div>
      </div>{/* end bgdc-charts-scroll */}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={async (emailData) => {
          try {
            const res = await sendReportEmail(emailData, { event_id: 10 });
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          }
        }}
        defaultSubject={`Blood Glucose Chart Report - ${new Date().toLocaleDateString()}`}
        defaultContent={`Blood Glucose Chart Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}`}
      />
    </div>
  );
};

export default DayChart;

